import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPercent, faListCheck, faBan } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import classes from './receipt-actions-modal.module.scss';

export type ReceiptActionKey = 'globalDiscount' | 'itemDiscount' | 'oth' | 'cancel';

export type ReceiptActionsModalProps = {
    opened: boolean;
    onClose: () => void;
    onSelectAction: (key: ReceiptActionKey) => void;
    isTakeAway?: boolean;
    // Quick-charge bill: expose ONLY ביטול (cancel), hide every other action.
    isQuickCharge?: boolean;
    // Optional explicit card set + order (first = rightmost in RTL layout).
    allowedActions?: ReceiptActionKey[];
    isSingleItem?: boolean;
};

const GiftIcon = () => (
    <svg width="34" height="34" viewBox="0 0 32 32" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 13h22v4H5z" />
        <path d="M7 17v9h18v-9" />
        <path d="M16 13v13" />
        <path d="M16 13s-1.5-6-5-6-3 4 0 4 5 2 5 2zM16 13s1.5-6 5-6 3 4 0 4-5 2-5 2z" />
    </svg>
);

type CardDef = { key: ReceiptActionKey; title: string; toneClass: string; icon: IconProp | null; svgIcon?: React.ReactNode };

// RTL: first in array = rightmost on screen
const CARDS: CardDef[] = [
    { key: 'oth', title: 'על חשבון הבית', toneClass: classes.cardOth, icon: null, svgIcon: <GiftIcon /> },
    { key: 'globalDiscount', title: 'הנחה כללית', toneClass: classes.cardWhole, icon: faPercent as IconProp },
    { key: 'itemDiscount', title: 'הנחה לפריטים', toneClass: classes.cardItems, icon: faListCheck as IconProp },
    { key: 'cancel', title: 'ביטול פריטים', toneClass: classes.cardCancel, icon: faBan as IconProp },
];

const ReceiptActionsModalComponent: React.FC<ReceiptActionsModalProps> = ({
    opened,
    onClose,
    onSelectAction,
    isTakeAway = false,
    isQuickCharge = false,
    allowedActions,
    isSingleItem = false,
}) => {
    if (!opened) return null;

    const cards = (() => {
        // allowedActions narrows the set; the QC/TA rules still apply on top of that subset.
        let base = CARDS;
        if (allowedActions && allowedActions.length > 0) {
            const cardByKey = new Map(CARDS.map((card) => [card.key, card] as const));
            base = allowedActions
                .map((key) => cardByKey.get(key) ?? null)
                .filter((card): card is CardDef => card !== null);
        }
        if (isQuickCharge) {
            return base.filter((card) => card.key === 'cancel');
        }
        if (isTakeAway) {
            return base.filter((card) => card.key !== 'cancel');
        }
        return base;
    })();

    return (
        <div className={classes.overlay} role="dialog" aria-modal="true" aria-label="פעולות ניהול" dir="rtl">
            <div className={classes.modal}>
                <header className={classes.header}>
                    <span className={classes.title}>פעולות ניהול</span>
                    <button
                        type="button"
                        className={classes.closeBtn}
                        onClick={onClose}
                        aria-label="סגור"
                        data-testid="receipt-actions-close"
                    >
                        ×
                    </button>
                </header>

                <div
                    className={classes.grid}
                    data-testid={isSingleItem ? 'receipt-actions-single-item' : 'receipt-actions-grid'}
                >
                    {cards.map((card) => (
                        <button
                            key={card.key}
                            type="button"
                            data-testid={`receipt-actions-card-${card.key}`}
                            className={`${classes.card} ${card.toneClass}`}
                            onClick={() => onSelectAction(card.key)}
                        >
                            <span className={classes.iconWrap}>
                                {card.svgIcon ?? <FontAwesomeIcon icon={card.icon!} />}
                            </span>
                            <span className={classes.cardTitle}>
                                {isSingleItem && card.key === 'cancel' ? 'ביטול פריט' : card.title}
                            </span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const ReceiptActionsModal = React.memo(ReceiptActionsModalComponent);
